import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { SessionService, ChatSession } from '../services/SessionService';
import { useAuth } from './AuthContext';
import logger from '../logging/logger';

interface GlobalSessionContextType {
  sessions: ChatSession[];
  currentSession: ChatSession | null;
  clearCurrentSession: () => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMoreSessions: boolean;
  loadSessions: () => Promise<void>;
  loadMoreSessions: () => Promise<void>;
  createSession: (title?: string) => Promise<ChatSession>;
  selectSession: (sessionId: string) => Promise<ChatSession | null>;
  renameSession: (sessionId: string, title: string) => Promise<void>;
  deleteSession: (sessionId: string) => Promise<void>;
  archiveSession: (sessionId: string) => Promise<void>;
  addNewSession: (session: ChatSession) => void;
  updateSessionTitleLocally: (sessionId: string, title: string) => void;
  pendingDelete: { sessionId: string; title: string } | null;
  requestDelete: (sessionId: string, title: string) => void;
  cancelDelete: () => void;
  confirmDelete: () => Promise<void>;
  generateSessionTitle: (sessionId: string, onTitleUpdate?: (title: string) => void) => Promise<{title: string}>;
  generateTitleFromMessage: (sessionId: string, message: string, onTitleUpdate?: (title: string) => void) => Promise<{title: string; skipped: boolean}>;
  generateTitleFromHistory: (sessionId: string, onTitleUpdate?: (title: string) => void) => Promise<{title: string}>;
}

const GlobalSessionContext = createContext<GlobalSessionContextType | undefined>(undefined);

interface GlobalSessionProviderProps {
  children: ReactNode;
}

export const GlobalSessionProvider: React.FC<GlobalSessionProviderProps> = ({ children }) => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreSessions, setHasMoreSessions] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [pendingDelete, setPendingDelete] = useState<{ sessionId: string; title: string } | null>(null);

  const { isAuthenticated, getToken, userUuid } = useAuth();
  
  // Memoize SessionService instance
  const sessionService = useMemo(() => new SessionService(), []);

  const loadSessions = useCallback(async () => {
    if (!isAuthenticated || !userUuid) return;
    
    try {
      setIsLoading(true);
      
      const token = await getToken();
      if (!token) return;
      
      const result = await sessionService.getSessions(userUuid, token, 30, 0);
      setSessions(result.sessions);
      setHasMoreSessions(result.hasMore);
      setCurrentOffset(30);
    } catch (error) {
      logger.error('Failed to load sessions:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, userUuid, getToken, sessionService]);

  const loadMoreSessions = useCallback(async () => {
    if (!isAuthenticated || !userUuid || isLoadingMore || !hasMoreSessions) return;
    
    try {
      setIsLoadingMore(true);
      
      const token = await getToken();
      if (!token) return;
      
      const result = await sessionService.getSessions(userUuid, token, 30, currentOffset);
      setSessions(prev => [...prev, ...result.sessions]);
      setHasMoreSessions(result.hasMore);
      setCurrentOffset(prev => prev + 30);
    } catch (error) {
      logger.error('Failed to load more sessions:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isAuthenticated, userUuid, isLoadingMore, hasMoreSessions, getToken, sessionService, currentOffset]);

  const createSession = useCallback(async (title?: string): Promise<ChatSession> => {
    if (!isAuthenticated || !userUuid) {
      throw new Error('User not authenticated');
    }
    
    try {
      const token = await getToken();
      if (!token) throw new Error('No token available');
      
      const newSession = await sessionService.createSession(userUuid, token, title);
      setSessions(prev => [newSession, ...prev]);
      setCurrentSession(newSession);
      return newSession;
    } catch (error) {
      logger.error('Failed to create session:', error);
      throw error;
    }
  }, [isAuthenticated, userUuid, getToken, sessionService]);

  // Reset the active session — used when entering a fresh New Chat so a backtest
  // run there cannot bind to a previously-viewed session.
  const clearCurrentSession = useCallback(() => setCurrentSession(null), []);

  const selectSession = useCallback(async (sessionId: string): Promise<ChatSession | null> => {
    if (!isAuthenticated || !userUuid) return null;
    
    try {
      const existingSession = sessions.find(s => s.id === sessionId);
      if (existingSession) {
        setCurrentSession(existingSession);
        return existingSession;
      }
      
      const token = await getToken();
      if (!token) return null;
      
      const session = await sessionService.getSession(sessionId, userUuid, token);
      setCurrentSession(session);
      
      setSessions(prev => {
        const exists = prev.some(s => s.id === sessionId);
        return exists ? prev : [session, ...prev];
      });
      
      return session;
    } catch (error) {
      logger.error('Failed to select session:', error);
      return null;
    }
  }, [isAuthenticated, userUuid, sessions, getToken, sessionService]);

  const renameSession = useCallback(async (sessionId: string, newTitle: string): Promise<void> => {
    if (!isAuthenticated || !userUuid) return;
    
    try {
      const token = await getToken();
      if (!token) return;
      
      const updatedSession = await sessionService.renameSession(sessionId, newTitle, userUuid, token);
      
      setSessions(prev => prev.map(session => 
        session.id === sessionId ? updatedSession : session
      ));
      
      if (currentSession?.id === sessionId) {
        setCurrentSession(updatedSession);
      }
    } catch (error) {
      logger.error('Failed to rename session:', error);
      throw error;
    }
  }, [isAuthenticated, userUuid, getToken, sessionService, currentSession?.id]);

  const deleteSession = useCallback(async (sessionId: string): Promise<void> => {
    if (!isAuthenticated || !userUuid) return;
    
    try {
      const token = await getToken();
      if (!token) return;
      
      await sessionService.deleteSession(sessionId, userUuid, token);
      
      setSessions(prev => prev.filter(session => session.id !== sessionId));
      
      if (currentSession?.id === sessionId) {
        setCurrentSession(null);
      }
    } catch (error) {
      logger.error('Failed to delete session:', error);
      throw error;
    }
  }, [isAuthenticated, userUuid, getToken, sessionService, currentSession?.id]);

  const archiveSession = useCallback(async (sessionId: string): Promise<void> => {
    if (!isAuthenticated || !userUuid) return;
    
    try {
      const token = await getToken();
      if (!token) return;
      
      await sessionService.archiveSession(sessionId, userUuid, token);
      
      setSessions(prev => prev.filter(session => session.id !== sessionId));
      
      if (currentSession?.id === sessionId) {
        setCurrentSession(null);
      }
    } catch (error) {
      logger.error('Failed to archive session:', error);
      throw error;
    }
  }, [isAuthenticated, userUuid, getToken, sessionService, currentSession?.id]);

  const addNewSession = useCallback((session: ChatSession) => {
    setSessions(prev => [session, ...prev]);
  }, []);

  const updateSessionTitleLocally = useCallback((sessionId: string, title: string) => {
    setSessions(prev => prev.map(session =>
      session.id === sessionId ? { ...session, title } : session
    ));
    if (currentSession?.id === sessionId) {
      setCurrentSession(prev => prev ? { ...prev, title } : prev);
    }
  }, [currentSession?.id]);

  const requestDelete = useCallback((sessionId: string, title: string) => {
    setPendingDelete({ sessionId, title });
  }, []);

  const cancelDelete = useCallback(() => {
    setPendingDelete(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;

    try {
      await deleteSession(pendingDelete.sessionId);
      setPendingDelete(null);
    } catch (error) {
      logger.error('Failed to confirm delete:', error);
      throw error;
    }
  }, [pendingDelete, deleteSession]);

  const generateSessionTitle = useCallback(async (sessionId: string, onTitleUpdate?: (title: string) => void): Promise<{title: string}> => {
    if (!isAuthenticated || !userUuid) {
      throw new Error('User not authenticated');
    }

    try {
      const token = await getToken();
      if (!token) throw new Error('No token available');

      return await sessionService.generateSessionTitleUsingAIAgent(sessionId, userUuid, token, onTitleUpdate);
    } catch (error) {
      logger.error('Failed to generate session title:', error);
      throw error;
    }
  }, [isAuthenticated, userUuid, getToken, sessionService]);

  const generateTitleFromMessage = useCallback(async (
    sessionId: string,
    message: string,
    onTitleUpdate?: (title: string) => void
  ): Promise<{title: string; skipped: boolean}> => {
    if (!isAuthenticated || !userUuid) {
      return { title: '', skipped: true };
    }

    try {
      const token = await getToken();
      if (!token) return { title: '', skipped: true };

      return await sessionService.generateTitleFromMessage(sessionId, userUuid, token, message, onTitleUpdate);
    } catch (error) {
      logger.error('Failed to generate title from message:', error);
      return { title: '', skipped: true };
    }
  }, [isAuthenticated, userUuid, getToken, sessionService]);

  const generateTitleFromHistory = useCallback(async (
    sessionId: string,
    onTitleUpdate?: (title: string) => void
  ): Promise<{title: string}> => {
    if (!isAuthenticated || !userUuid) {
      throw new Error('User not authenticated');
    }

    try {
      const token = await getToken();
      if (!token) throw new Error('No token available');

      return await sessionService.generateTitleFromHistory(sessionId, userUuid, token, onTitleUpdate);
    } catch (error) {
      logger.error('Failed to generate title from history:', error);
      throw error;
    }
  }, [isAuthenticated, userUuid, getToken, sessionService]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const contextValue: GlobalSessionContextType = useMemo(() => ({
    sessions,
    currentSession,
    clearCurrentSession,
    isLoading,
    isLoadingMore,
    hasMoreSessions,
    loadSessions,
    loadMoreSessions,
    createSession,
    selectSession,
    renameSession,
    deleteSession,
    archiveSession,
    addNewSession,
    updateSessionTitleLocally,
    pendingDelete,
    requestDelete,
    cancelDelete,
    confirmDelete,
    generateSessionTitle,
    generateTitleFromMessage,
    generateTitleFromHistory,
  }), [
    sessions,
    currentSession,
    clearCurrentSession,
    isLoading,
    isLoadingMore,
    hasMoreSessions,
    loadSessions,
    loadMoreSessions,
    createSession,
    selectSession,
    renameSession,
    deleteSession,
    archiveSession,
    addNewSession,
    updateSessionTitleLocally,
    pendingDelete,
    requestDelete,
    cancelDelete,
    confirmDelete,
    generateSessionTitle,
    generateTitleFromMessage,
    generateTitleFromHistory,
  ]);

  return (
    <GlobalSessionContext.Provider value={contextValue}>
      {children}
    </GlobalSessionContext.Provider>
  );
};

export const useGlobalSession = (): GlobalSessionContextType => {
  const context = useContext(GlobalSessionContext);
  if (context === undefined) {
    throw new Error('useGlobalSession must be used within a GlobalSessionProvider');
  }
  return context;
};